/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Modal, ModalBody, ModalFooter, ModalHeader, Module, Node, ITableRowProps, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IBusinessOrganizationListViewProps, InputID, IBusinessFormItem } from '@msdyn365-commerce-modules/business-organization-list';
import { IKrannichCBusinessOrganizationListProps } from './krannich-c-business-organization-list.props.autogenerated';
import { IFormViewProps } from './krannich-c-business-organization-list';

const BusinessFormItem: React.FC<IBusinessFormItem> = ({ wrapper, label, errorMessage, input }) => {
    return (
        <Node {...wrapper}>
            {label}
            {errorMessage}
            {input}
        </Node>
    );
};

const BusinessUserForm: React.FC<IFormViewProps> = (props: IFormViewProps) => {
    const { modalClassName, FormWrapper, modalHeading, modalDescription, inputs, buttons, errorMessage, toggle } = props;

    const modalBody = (
        <>
            {!modalDescription && (
                <p className='ms-business-organization-list__form-all-required-msg'>*All Fields are required to update a user</p>
            )}
            {modalDescription}
            <Node {...FormWrapper}>
                {inputs &&
                    inputs.map((item: IBusinessFormItem) => {
                        // VSI-CUSTOMIZATION WE DONOT NEED SPENDING LIMIT IN OUR FORM
                        if (item.key !== 'SpendingLimit') {
                            return <BusinessFormItem {...item} key={item.key} />;
                        }
                        return null;
                    })}
                {errorMessage &&
                    errorMessage.map((error: React.ReactNode, index: number) => {
                        return <React.Fragment key={index}>{error}</React.Fragment>;
                    })}
            </Node>
        </>
    );

    const modalFooter =
        buttons &&
        buttons.map((button: React.ReactNode, index: number) => {
            return <React.Fragment key={index}>{button}</React.Fragment>;
        });

    return _renderModal(modalClassName, true, modalHeading, modalBody, modalFooter, toggle);
};

const BusinessUserDetails: React.FC<IFormViewProps> = (props: IFormViewProps) => {
    const {
        modalClassName,
        FormWrapper,
        modalHeading,
        modalDescription,
        selectedUser,
        buttons,
        errorMessage,
        resources,
        toggle,
        showConfirmationMessage
    } = props;
    let modalBody = null;

    if (!selectedUser) {
        modalBody = errorMessage;
    } else {
        const { FirstName, LastName } = selectedUser;
        if (selectedUser?.Status === 'Pending') {
            modalBody = (
                <span className='restrict-user-message'>
                    {/* todo: need to convert back to resources  */}
                    User in pending state cannot be deleted
                </span>
            );
        } else {
            modalBody = (
                <>
                    {modalDescription}
                    <Node tag='ul' className={`${FormWrapper.className}-list`}>
                        {!showConfirmationMessage && (
                            <Node tag='li' className={`${FormWrapper.className}-list-item-name del-user`}>
                                <div className='name-contianer'>
                                    <div className='_first-name'>
                                        <div className='detete-label'>First Name:</div>
                                        <div className='delete-value'>{FirstName}</div>
                                    </div>
                                    <div className='_last-name'>
                                        <div className='detete-label'>Last Name:</div>
                                        <div className='delete-value'>{LastName}</div>
                                    </div>
                                </div>
                            </Node>
                        )}
                        {!showConfirmationMessage &&
                            Object.keys(selectedUser).map((data: string) => {
                                if (data === InputID.FirstName || data === InputID.LastName) {
                                    return null;
                                }
                                const label = resources[`table${data}HeadingText`] || data;
                                return (
                                    <Node tag='li' className={`${FormWrapper.className}-list-item`} key={props.selectedUser}>
                                        <div className='detete-label'>{label}:</div>
                                        <div className='delete-value'>{selectedUser[String(data)]}</div>
                                    </Node>
                                );
                            })}
                        {showConfirmationMessage && (
                            <span className='confirmation-message'>
                                {/* todo: need to convert back to resources  */}
                                The request has been accepted, the user will be deleted in a few minutes
                            </span>
                        )}
                    </Node>
                </>
            );
        }
    }

    let modalFooter;
    if (selectedUser?.Status === 'Pending' || showConfirmationMessage) {
        modalFooter = null;
    } else {
        modalFooter =
            buttons &&
            buttons.map((button: React.ReactNode, index: number) => {
                return <React.Fragment key={index}>{button}</React.Fragment>;
            });
    }

    return _renderModal(modalClassName, true, modalHeading, modalBody, modalFooter, toggle);
};

const _getForm = (type: string, formProps: IFormViewProps, toggleModal?: () => void) => {
    switch (type) {
        case 'ADD':
        case 'EDIT':
            return <BusinessUserForm {...formProps} toggle={toggleModal} />;
        case 'VIEW':
        case 'REMOVE':
            return <BusinessUserDetails {...formProps} toggle={toggleModal} />;
        default:
            return null;
    }
};

const _renderModal = (
    className: string,
    isModalOpen: boolean,
    headingSection: React.ReactNode,
    bodySection: React.ReactNode,
    footerSection: React.ReactNode,
    toggleModal?: () => void
) => {
    return (
        <Modal
            className='msc-modal-input-required'
            wrapClassName={className}
            autoFocus
            fade={false}
            applicationNode='renderPage'
            isOpen={isModalOpen}
            toggle={toggleModal}
        >
            <ModalHeader toggle={toggleModal}>{headingSection}</ModalHeader>
            <ModalBody>{bodySection}</ModalBody>
            <ModalFooter>{footerSection}</ModalFooter>
        </Modal>
    );
};

const BusinessOrganizationList: React.FC<IBusinessOrganizationListViewProps & IKrannichCBusinessOrganizationListProps<{}>> = props => {
    const {
        OrganizationUserList,
        moduleContainer,
        heading,
        noUsersText,
        addUserButton,
        organizationListTable,
        form,
        formType,
        toggleModal,
        // requestAccountStatementButton,
        requestStatementModal,
        config: { toShowOrgUsersNumber }
    } = props;

    // ********** VSI CUSTOMIZATION START in this we are using same logic which we are used in our
    // krannich-c-account-management-address.view.tsx for our my addreses all comments are added for spcific login in mention file *********

    // 150 to 152 Extract Data from coming tsx
    const organizationListTableJSX = organizationListTable as JSX.Element;
    const organizationListTableJSXProps = organizationListTableJSX.props;
    const organizationListTableJSXPropsRow = organizationListTableJSXProps.rows as ITableRowProps[];

    //Use States for set display data
    const [toShowData, setToShowData] = React.useState<ITableRowProps[]>();
    const [toShowOrgUsers, setToShowOrgUsers] = React.useState<ITableRowProps[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [isDisabled, setIsDisabled] = React.useState<boolean>(false);
    const [counter, setCounter] = React.useState<number>(1);
    const [quan, setQuan] = React.useState<number>(Number(toShowOrgUsersNumber) || 2);
    const [toShowItemNumber, setToShowItemNumber] = React.useState(quan);

    // use to set tsx data into local state
    React.useEffect(() => {
        const arr: ITableRowProps[] = [];
        for (let index = 0; index < organizationListTableJSXPropsRow.length; ++index) {
            // eslint-disable-next-line security/detect-object-injection
            const ob = _toFindStatus(organizationListTableJSXPropsRow[index]?.row, 'Removed');
            if (ob) {
                organizationListTableJSXPropsRow[Number(index)] && arr.push(organizationListTableJSXPropsRow[Number(index)]);
            }
        }
        setToShowData(arr);
    }, []);

    //set quantity and call function
    React.useEffect(() => {
        setQuan(Number(toShowOrgUsersNumber) || 2);
        if (toShowData) {
            addOrgUsers();
        }
    }, [toShowData]);

    React.useEffect(() => {
        addOrgUsers();
    }, [toShowItemNumber]);

    React.useEffect(() => {
        if (toShowData) {
            toShowData?.length === toShowOrgUsers?.length ? setIsDisabled(true) : setIsDisabled(false);
        }
    }, [toShowOrgUsers, toShowData]);

    React.useEffect(() => {
        setToShowItemNumber(quan * counter);
    }, [counter]);

    const handleToShowItemNumer = () => {
        setCounter(counter + 1);
    };

    enum TableDataType {
        Number = 'Number',
        Text = 'Text',
        Price = 'Price'
    }

    interface ITableItemProps {
        id: string;
        type: TableDataType;
        value: boolean | number | string;
        className?: string;
    }

    const _toFindStatus = (rowDataArray: ITableItemProps[], toFindAttribute: string) => {
        let toCheckValue: boolean = false;
        rowDataArray &&
            rowDataArray?.find((e: any) => {
                if (e.id === 'Status') {
                    if (e.value === toFindAttribute) {
                        toCheckValue = false;
                    } else {
                        toCheckValue = true;
                    }
                }
            });
        return toCheckValue;
    };

    const addOrgUsers = () => {
        setIsLoading(true);
        const arr: ITableRowProps[] = [];
        for (let index = 0; index < toShowItemNumber; ++index) {
            // eslint-disable-next-line security/detect-object-injection
            if (toShowData) {
                toShowData[Number(index)] && arr.push(toShowData[Number(index)]);
            }
        }
        setToShowOrgUsers(arr);
        setIsLoading(false);
    };

    const organizationListTableCloned =
        organizationListTable &&
        React.cloneElement(organizationListTable as JSX.Element, {
            showCheckBoxes: false,
            rows: toShowOrgUsers
        });

    // ********** VSI CUSTOMIZATION START *********
    const ButtonList: React.ReactNode = (
        <Node className='ms-business-organization-list__buttonBar'>
            {addUserButton}
            {/* VSI-CUSOTMIZATION TO HIDE REQUEST A STATMENT DROPDOWN */}
            {/* {requestAccountStatementButton} */}
        </Node>
    );

    if (noUsersText) {
        return (
            <Module {...OrganizationUserList}>
                <Node {...moduleContainer}>
                    {heading}
                    {ButtonList}
                    {requestStatementModal}
                    {noUsersText}
                </Node>
            </Module>
        );
    }

    return (
        <Module {...OrganizationUserList}>
            <Node {...moduleContainer}>
                {isLoading ? (
                    <Waiting />
                ) : (
                    <>
                        {heading}
                        {ButtonList}
                        {requestStatementModal}
                        {organizationListTableCloned}
                        <button
                            className={`ms-order-history__btn-more  msc-btn ${isDisabled ? 'ctx-disabled-view-more' : ''}`}
                            onClick={() => {
                                handleToShowItemNumer();
                            }}
                            disabled={isDisabled}
                        >
                            View More
                        </button>
                    </>
                )}
            </Node>
            {form && _getForm(formType, form, toggleModal)}
        </Module>
    );
};

export default BusinessOrganizationList;
